export function isSameOrigin(url) {
    try {
        return new URL(url, location.href).origin === location.origin;
    } catch {
        return false;
    }
}

export default function setupSessionTimeout() {
    const timeoutMinutes = window.sessionTimeoutMinutes;
    const idleTimeout = timeoutMinutes * 60 * 1000;
    const warningMinutes = 2;
    const warningTimeout = idleTimeout - warningMinutes * 60 * 1000;
    const lastRequestStorageKey = 'sessionLastRequestMs';
    let timeoutTimer;
    let warningTimer;
    let hasShownWarning = false;

    function triggerLoginRedirect() {
        window.location.reload();
    }

    function getCurrentTime() {
        return new Date().getTime();
    }

    function setLastRequestTime() {
        const currentTime = getCurrentTime();
        localStorage.setItem(lastRequestStorageKey, currentTime);
    }

    function getLastRequestTime() {
        return parseInt(localStorage.getItem(lastRequestStorageKey));
    }

    function getTimeSinceLastRequest() {
        const diffTimeout = new Date().getTime() - getLastRequestTime();
        return diffTimeout;
    }

    function hasTimeRemaining(timeout) {
        const diffTimeout = getTimeSinceLastRequest();
        return timeout - diffTimeout > 0;
    }

    function showExpirationWarning() {
        if (hasShownWarning) return;
        hasShownWarning = true;

        const message = `Your session will expire in ${warningMinutes} minutes.`;

        const currentlyShowing = Array.from(document.querySelectorAll('.k-dialog-content'))
            .some(el => el.textContent.trim() === message);

        if (currentlyShowing) return;

        kendo.alert(message);
    }

    function resetTimer() {
        clearTimeout(timeoutTimer);
        clearTimeout(warningTimer);

        const diffTimeout = getTimeSinceLastRequest();

        if (hasShownWarning && hasTimeRemaining(warningTimeout)) {
            hasShownWarning = false;
        }

        if (!hasTimeRemaining(idleTimeout + 3000)) {
            triggerLoginRedirect();
            return;
        }

        timeoutTimer = setTimeout(() => {
            if (hasTimeRemaining(idleTimeout + 3000)) {
                hasShownWarning = false;
                resetTimer();
                return;
            }

            triggerLoginRedirect();
        }, idleTimeout - diffTimeout + 3000);

        warningTimer = setTimeout(() => {
            if (hasTimeRemaining(warningTimeout)) {
                resetTimer();
                return;
            }

            showExpirationWarning();
        }, warningTimeout - diffTimeout);
    }

    (function() {
        const responseFn = status => {
            if (status === 401) {
                triggerLoginRedirect();
                return;
            }

            setLastRequestTime();
            resetTimer();
        };

        // Capture Fetch API constructors from window rather than bare global names.
        // Bare globals like Request/Headers resolve against Node's global object in jest's
        // module context, where jest-environment-jsdom may not have registered them.
        const RequestClass = typeof window !== 'undefined' ? window.Request : null;
        const HeadersClass = typeof window !== 'undefined' ? window.Headers : null;

        const originalXhrOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function(method, url) {
            this._isSameOrigin = isSameOrigin(url);
            this.addEventListener('load', () => {
                responseFn(this.status);
            });
            originalXhrOpen.apply(this, arguments);
        };

        const originalXhrSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.send = function() {
            if (this._isSameOrigin && !this._requestedWithSet) {
                this.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
            }
            originalXhrSend.apply(this, arguments);
        };

        const originalXhrSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
        XMLHttpRequest.prototype.setRequestHeader = function(name, value) {
            // Call original first so the flag is only set when the header is actually accepted.
            // Setting the flag before the call would incorrectly suppress header injection if
            // the original throws (e.g. InvalidStateError when called before open()).
            originalXhrSetRequestHeader.apply(this, arguments);
            if (name.toLowerCase() === 'x-requested-with') {
                this._requestedWithSet = true;
            }
        };

        const originalFetch = window.fetch;
        window.fetch = async function(input, init) {
            const url = (RequestClass && input instanceof RequestClass) ? input.url : String(input);
            if (isSameOrigin(url) && HeadersClass) {
                const baseHeaders = (RequestClass && input instanceof RequestClass) ? input.headers : null;
                const headers = baseHeaders ? new HeadersClass(baseHeaders) : new HeadersClass();
                if ((init || {}).headers) {
                    new HeadersClass(init.headers).forEach((value, key) => headers.set(key, value));
                }
                if (!headers.has('X-Requested-With')) {
                    headers.set('X-Requested-With', 'XMLHttpRequest');
                }
                init = { ...init, headers };
            }
            const response = await originalFetch.call(this, input, init);
            responseFn(response.status);
            return response;
        };
    })();

    (function() {
        const pingIntervalMs = 60 * 1000;
        let isActive = false;

        const activityEvents = ['keydown', 'click', 'touchstart', 'input', 'change'];
        activityEvents.forEach(evt => {
            document.addEventListener(
                evt,
                () => {
                    isActive = true;
                },
                { passive: true, capture: true }
            );
        });

        setInterval(() => {
            if (!isActive) return;

            isActive = false;
            const timeSinceLast = getTimeSinceLastRequest();

            if (timeSinceLast < pingIntervalMs) return;

            fetch('/session/keep-alive', { method: 'GET', credentials: 'same-origin', cache: 'no-store' });
        }, pingIntervalMs);
    })();

    setLastRequestTime();
    window.addEventListener('load', resetTimer);
    window.addEventListener('focus', resetTimer);

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            resetTimer();
        }
    });
}
